import React, { useEffect, useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  SlidersHorizontal, X, ChevronDown, ChevronRight, Search, RotateCcw, Check, Ban,
  Hash, Star, Loader2, Download, PanelLeftClose, PanelLeftOpen, PanelTop, PanelLeft, Columns2,
  BookmarkPlus, Bookmark, Trash2, Users, Target,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { DATE_PRESETS } from "@/lib/datePresets";
import { activeChips, inPanel, useFacets, BLANK } from "@/lib/filterPanel";
import { clearDescendants, ScopeEditor } from "@/components/bi/FilterBar";

// Item 98 — the reusable filter panel. Same `global_filters` definitions as the filter bar, shown
// as a 280px sticky sidebar on desktop and an off-canvas drawer under 860px. Counts come from the
// governed facets endpoint, so what you see always respects row- and column-level security.

const PANEL_W = 280;
const nf = new Intl.NumberFormat();

function Section({ f, count, children, defaultOpen = true, onClear, active, target = null }) {
  const [open, setOpen] = useState(f.collapsed ? false : defaultOpen);
  return (
    <div data-testid={`filter-panel-section-${f.id}`} className="border-b border-[color:var(--glass-border)] last:border-b-0">
      <div className="w-full flex items-center gap-1.5 px-3 py-2.5 group">
        <button data-testid={`filter-panel-section-toggle-${f.id}`} onClick={() => setOpen((o) => !o)}
          aria-expanded={open} className="flex-1 min-w-0 flex items-center gap-1.5 text-left">
          {open ? <ChevronDown className="w-3.5 h-3.5 text-[color:var(--ink-3)] shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-[color:var(--ink-3)] shrink-0" />}
          <span className="flex-1 text-[11px] font-semibold uppercase tracking-wide text-[color:var(--ink-2)] truncate">{f.label || f.field}</span>
        </button>
        {active && (
          <button type="button" data-testid={`filter-panel-clear-${f.id}`} onClick={onClear}
            className="shrink-0 text-[10px] text-[color:var(--brand-ocean)] hover:underline">clear</button>
        )}
        {count != null && !active && <span className="shrink-0 text-[10px] text-[color:var(--ink-3)]">{nf.format(count)}</span>}
        {target && <span className="shrink-0 flex items-center">{target}</span>}
      </div>
      {open && <div className="px-3 pb-3">{children}</div>}
    </div>
  );
}

function CheckboxFacet({ f, options, value, onChange, showCount }) {
  const arr = Array.isArray(value) ? value : (value?.v || []);
  const exclude = !Array.isArray(value) && !!value?.exclude;
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(false);
  const parentRef = useRef(null);

  const list = useMemo(() => {
    const base = f.show_blank === false ? options.filter((o) => !o.blank) : options;
    if (!q.trim()) return base;
    const needle = q.trim().toLowerCase();
    return base.filter((o) => String(o.value).toLowerCase().includes(needle));
  }, [options, q, f.show_blank]);

  const initial = Number(f.top_n) || 8;
  const shown = expanded ? list : list.slice(0, initial);
  const virtual = expanded && shown.length > 80;

  const set = (next) => onChange(exclude ? { v: next, exclude: true } : next);
  const toggle = (v) => {
    const s = new Set(arr);
    s.has(v) ? s.delete(v) : s.add(v);
    set([...s]);
  };

  const rowVirtualizer = useVirtualizer({
    count: virtual ? shown.length : 0,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 28,
    overscan: 8,
  });

  const Row = ({ o }) => (
    <label data-testid={`filter-opt-${f.id}-${o.value}`}
      className="flex items-center gap-2 py-1 cursor-pointer group rounded hover:bg-[color:var(--surface-2)] px-1 -mx-1">
      <Checkbox className="h-3.5 w-3.5" checked={arr.includes(o.value)} onCheckedChange={() => toggle(o.value)} />
      <span className={cn("text-[12px] truncate flex-1", o.blank ? "text-[color:var(--ink-3)] italic" : "text-[color:var(--ink-2)]")}>{o.value}</span>
      {showCount && <span className="text-[10px] text-[color:var(--ink-3)] tabular-nums">{nf.format(o.count)}</span>}
    </label>
  );

  return (
    <div className="space-y-1.5">
      {(options.length > 8 || q) && (
        <div className="relative">
          <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-[color:var(--ink-3)]" />
          <Input data-testid={`filter-search-${f.id}`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…"
            className="h-7 pl-6 text-[11px] bg-[color:var(--surface-2)]" />
        </div>
      )}
      {virtual ? (
        <div ref={parentRef} data-testid={`filter-virtual-${f.id}`} className="max-h-[240px] overflow-y-auto">
          <div style={{ height: rowVirtualizer.getTotalSize(), position: "relative" }}>
            {rowVirtualizer.getVirtualItems().map((vi) => (
              <div key={vi.key} style={{ position: "absolute", top: 0, left: 0, width: "100%", height: vi.size, transform: `translateY(${vi.start}px)` }}>
                <Row o={shown[vi.index]} />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className={cn(expanded && "max-h-[240px] overflow-y-auto")}>
          {shown.map((o) => <Row key={String(o.value)} o={o} />)}
          {!shown.length && <p className="text-[11px] text-[color:var(--ink-3)] py-1">No matches</p>}
        </div>
      )}
      {list.length > initial && (
        <button data-testid={`filter-showmore-${f.id}`} onClick={() => setExpanded((e) => !e)}
          className="text-[11px] text-[color:var(--brand-ocean)] hover:underline">
          {expanded ? "Show less" : `Show all ${list.length}`}
        </button>
      )}
      {(f.exclude === true || (f.type === "checkbox" && f.exclude !== false)) && (
        <label data-testid={`filter-exclude-${f.id}`} className="flex items-center gap-1.5 pt-1 cursor-pointer text-[10px] text-[color:var(--ink-3)]">
          <Checkbox className="h-3 w-3" checked={exclude}
            onCheckedChange={(v) => onChange(arr.length ? (v ? { v: arr, exclude: true } : arr) : (v ? { v: [], exclude: true } : null))} />
          <Ban className="w-3 h-3" />Exclude these instead
        </label>
      )}
    </div>
  );
}

function NumFacet({ f, value, buckets, stats, onChange }) {
  const preset = value && !Array.isArray(value) ? value.preset : "";
  const custom = Array.isArray(value) ? value : [null, null];
  return (
    <div className="space-y-1.5">
      {(f.ranges || []).map((r) => (
        <label key={r.key} data-testid={`filter-range-${f.id}-${r.key}`} className="flex items-center gap-2 py-0.5 cursor-pointer">
          <input type="radio" className="accent-[color:var(--brand-ocean)]" checked={preset === r.key}
            onChange={() => onChange({ preset: r.key })} />
          <span className="text-[12px] text-[color:var(--ink-2)] flex-1 truncate">{r.label}</span>
          {buckets[r.key] != null && <span className="text-[10px] text-[color:var(--ink-3)] tabular-nums">{nf.format(buckets[r.key])}</span>}
        </label>
      ))}
      <div className="flex items-center gap-1 pt-1">
        <Hash className="w-3 h-3 text-[color:var(--ink-3)]" />
        <Input data-testid={`filter-num-min-${f.id}`} type="number" value={custom[0] ?? ""} placeholder={stats.min != null ? String(Math.floor(stats.min)) : "min"}
          onChange={(e) => onChange([e.target.value === "" ? null : Number(e.target.value), custom[1]])}
          className="h-7 text-[11px] bg-[color:var(--surface-2)]" />
        <span className="text-[color:var(--ink-3)] text-[11px]">–</span>
        <Input data-testid={`filter-num-max-${f.id}`} type="number" value={custom[1] ?? ""} placeholder={stats.max != null ? String(Math.ceil(stats.max)) : "max"}
          onChange={(e) => onChange([custom[0], e.target.value === "" ? null : Number(e.target.value)])}
          className="h-7 text-[11px] bg-[color:var(--surface-2)]" />
      </div>
    </div>
  );
}

function TierFacet({ f, value, counts, onChange }) {
  return (
    <div className="space-y-1">
      {(f.levels || []).map((l) => (
        <label key={l.key} data-testid={`filter-tier-${f.id}-${l.key}`} className="flex items-center gap-2 py-0.5 cursor-pointer">
          <input type="radio" className="accent-[color:var(--brand-ocean)]" checked={String(value) === String(l.key)} onChange={() => onChange(l.key)} />
          <span className="text-[12px] text-[color:var(--ink-2)] flex items-center gap-1 flex-1">
            {f.icon === "star" && <Star className="w-3 h-3 text-[color:var(--brand-amber)]" />}{l.label || l.key}
          </span>
          {counts[String(l.key)] != null && <span className="text-[10px] text-[color:var(--ink-3)] tabular-nums">{nf.format(counts[String(l.key)])}</span>}
        </label>
      ))}
    </div>
  );
}

function DateFacet({ f, value, onChange }) {
  const isLastN = value && typeof value === "object" && !Array.isArray(value) && value.preset === "last_n";
  const preset = isLastN ? "last_n" : typeof value === "string" ? value : Array.isArray(value) ? "custom" : "__none__";
  const custom = Array.isArray(value) ? value : ["", ""];
  const quick = (f.quick || ["today", "last_7_days", "this_month", "this_quarter", "this_year"]);
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap gap-1">
        {quick.map((code) => {
          const p = DATE_PRESETS.find((x) => x.value === code);
          if (!p) return null;
          return (
            <button key={code} data-testid={`filter-date-quick-${f.id}-${code}`} onClick={() => onChange(preset === code ? null : code)}
              className={cn("text-[10px] px-2 py-1 rounded-full border transition-colors",
                preset === code ? "bg-[color:var(--brand-ocean)] text-white border-transparent" : "border-[color:var(--glass-border)] text-[color:var(--ink-2)] hover:bg-[color:var(--surface-2)]")}>
              {p.label}
            </button>
          );
        })}
      </div>
      <Select value={preset} onValueChange={(v) => onChange(v === "__none__" ? null : v === "custom" ? ["", ""] : v === "last_n" ? { preset: "last_n", n: 14 } : v)}>
        <SelectTrigger data-testid={`filter-date-preset-${f.id}`} className="h-7 text-[11px] bg-[color:var(--surface-2)]"><SelectValue placeholder="All time" /></SelectTrigger>
        <SelectContent className="max-h-[260px]">
          <SelectItem value="__none__">All time</SelectItem>
          {DATE_PRESETS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
        </SelectContent>
      </Select>
      {isLastN && (
        <Input data-testid={`filter-date-lastn-${f.id}`} type="number" min={1} value={value.n ?? ""}
          onChange={(e) => onChange({ preset: "last_n", n: e.target.value === "" ? "" : Number(e.target.value) })}
          className="h-7 text-[11px] bg-[color:var(--surface-2)]" placeholder="days" />
      )}
      {preset === "custom" && (
        <div className="flex items-center gap-1">
          <Input data-testid={`filter-date-from-${f.id}`} type="date" value={custom[0] || ""} onChange={(e) => onChange([e.target.value, custom[1]])} className="h-7 text-[11px] bg-[color:var(--surface-2)]" />
          <Input data-testid={`filter-date-to-${f.id}`} type="date" value={custom[1] || ""} onChange={(e) => onChange([custom[0], e.target.value])} className="h-7 text-[11px] bg-[color:var(--surface-2)]" />
        </div>
      )}
    </div>
  );
}

export function FilterPanel({
  filtersDef = [], values = {}, onChange, onClearAll, publicToken = null, editable = false,
  settings = {}, tabId = null, open = true, onOpenChange, drawer = false, onExportCsv,
  sets = null, onApplySet, onSaveSet, onDeleteSet,
  widgets = [], onUpdateDef = null, onApplyAll = null,
}) {
  const defs = useMemo(() => filtersDef.filter((f) => inPanel(f) && (editable || !f.hidden)
    && (!f.tab_id || !tabId || f.tab_id === tabId)), [filtersDef, editable, tabId]);
  const showCounts = settings.counts !== false;
  // Item 33: one common Apply for the whole panel — selections stage until it is pressed.
  const applyMode = settings.apply_mode === "manual" && !!onApplyAll;
  const [draft, setDraft] = useState(null);
  const effValues = applyMode && draft ? draft : values;
  const dirty = applyMode && !!draft && JSON.stringify(draft) !== JSON.stringify(values);
  const facets = useFacets({ defs, values: effValues, publicToken, enabled: open && defs.length > 0 });
  const chips = activeChips(defs, effValues);
  const searchDef = defs.find((f) => f.type === "search");
  const total = facets.primaryTotal;
  // Item 33: find a filter when the panel has many sections
  const [find, setFind] = useState("");
  const matches = (f) => !find.trim() || String(f.label || f.field || "").toLowerCase().includes(find.trim().toLowerCase());

  const change = (fid, v) => {
    const next = clearDescendants(filtersDef, { ...effValues, [fid]: v }, fid);
    if (applyMode) { setDraft(next); return; }
    onChange(fid, v, next);
  };
  const applyDraft = () => { if (draft) onApplyAll(draft); setDraft(null); };
  const cancelDraft = () => setDraft(null);

  if (!defs.length) return null;

  const body = (
    <>
      <div className="shrink-0 flex items-center gap-1.5 px-3 py-2.5 border-b border-[color:var(--glass-border)]">
        <SlidersHorizontal className="w-3.5 h-3.5 text-[color:var(--brand-ocean)]" />
        <span className="text-xs font-semibold text-[color:var(--ink)] flex-1">Filters</span>
        {facets.loading && <Loader2 data-testid="filter-panel-loading" className="w-3 h-3 animate-spin text-[color:var(--ink-3)]" />}
        {chips.length > 0 && (
          <button data-testid="filter-panel-clear-all" onClick={() => { setDraft(null); onClearAll(); }} title="Clear all filters"
            className="flex items-center gap-1 text-[10px] text-[color:var(--brand-ocean)] hover:underline">
            <RotateCcw className="w-3 h-3" />Clear
          </button>
        )}
        {drawer ? (
          <button data-testid="filter-panel-drawer-close" onClick={() => onOpenChange(false)} className="p-1 text-[color:var(--ink-3)] hover:text-[color:var(--ink)]"><X className="w-4 h-4" /></button>
        ) : (
          <button data-testid="filter-panel-hide" onClick={() => onOpenChange(false)} title="Hide filters"
            className="p-1 text-[color:var(--ink-3)] hover:text-[color:var(--ink)]"><PanelLeftClose className="w-3.5 h-3.5" /></button>
        )}
      </div>

      {defs.filter((f) => f.type !== "search").length > 3 && (
        <div className="shrink-0 px-3 py-2 border-b border-[color:var(--glass-border)]">
          <div className="relative">
            <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-[color:var(--ink-3)]" />
            <Input data-testid="filter-panel-find" value={find} onChange={(e) => setFind(e.target.value)}
              placeholder="Find a filter…" className="h-7 pl-6 pr-6 text-[11px] bg-[color:var(--surface-2)]" />
            {find ? (
              <button data-testid="filter-panel-find-clear" onClick={() => setFind("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[color:var(--ink-3)] hover:text-[color:var(--danger)]"><X className="w-3 h-3" /></button>
            ) : null}
          </div>
        </div>
      )}

      {sets && (
        <SavedSets sets={sets} chips={chips} editable={editable}
          onApply={onApplySet} onSave={onSaveSet} onDelete={onDeleteSet} />
      )}

      {searchDef && (
        <div className="shrink-0 px-3 py-2 border-b border-[color:var(--glass-border)]">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-[color:var(--ink-3)]" />
            <Input data-testid="filter-panel-global-search" value={effValues[searchDef.id] || ""}
              onChange={(e) => change(searchDef.id, e.target.value || null)}
              placeholder={searchDef.placeholder || settings.search?.placeholder || "Search everything…"}
              className="h-8 pl-7 pr-6 text-[12px] bg-[color:var(--surface-2)]" />
            {effValues[searchDef.id] ? (
              <button data-testid="filter-panel-global-search-clear" onClick={() => change(searchDef.id, null)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[color:var(--ink-3)] hover:text-[color:var(--danger)]"><X className="w-3 h-3" /></button>
            ) : null}
          </div>
          {searchDef.fields?.length ? (
            <p className="mt-1 text-[10px] text-[color:var(--ink-3)] truncate">Searches {searchDef.fields.join(", ")}</p>
          ) : null}
        </div>
      )}

      {settings.chips !== false && chips.length > 0 && (
        <div data-testid="filter-panel-chips" className="shrink-0 flex flex-wrap gap-1 px-3 py-2 border-b border-[color:var(--glass-border)]">
          {chips.map((c) => (
            <span key={c.id} data-testid={`filter-panel-chip-${c.id}`}
              className={cn("inline-flex items-center gap-1 text-[10px] rounded-full pl-2 pr-1 py-0.5",
                c.exclude ? "bg-[color:var(--danger)]/10 text-[color:var(--danger)]" : "bg-[color:var(--surface-2)] text-[color:var(--ink-2)]")}>
              <span className="opacity-70">{c.label}{c.exclude ? " ≠" : ":"}</span>
              <span className="truncate max-w-[110px]">{c.text}</span>
              <button data-testid={`filter-panel-chip-remove-${c.id}`} onClick={() => change(c.id, null)}
                className="p-0.5 rounded-full hover:bg-[color:var(--surface)] hover:text-[color:var(--danger)]"><X className="w-2.5 h-2.5" /></button>
            </span>
          ))}
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain" data-testid="filter-panel-sections">
        {!defs.filter((f) => f.type !== "search" && matches(f)).length && (
          <p className="px-3 py-3 text-[11px] text-[color:var(--ink-3)]">No filter matches “{find}”.</p>
        )}
        {defs.filter((f) => f.type !== "search" && matches(f)).map((f) => {
          const v = effValues[f.id];
          const targetEl = editable && onUpdateDef && f.dataset_id
            ? <ScopeEditor f={f} widgets={widgets} onUpdateDef={onUpdateDef} />
            : null;
          const active = !(v == null || v === "" || (Array.isArray(v) && !v.length) || (f.type === "toggle" && !v));
          const clear = () => change(f.id, null);
          if (f.type === "toggle") {
            const c = facets.toggleCount(f);
            return (
              <div key={f.id} data-testid={`filter-panel-section-${f.id}`} className="border-b border-[color:var(--glass-border)] px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
                    <Checkbox data-testid={`filter-toggle-${f.id}`} className="h-3.5 w-3.5" checked={!!v} onCheckedChange={(x) => change(f.id, !!x || null)} />
                    <span className="text-[12px] text-[color:var(--ink-2)] flex-1 truncate">{f.label || f.field}</span>
                    {showCounts && c != null && <span className="text-[10px] text-[color:var(--ink-3)] tabular-nums">{nf.format(c)}</span>}
                  </label>
                  {targetEl}
                </div>
              </div>
            );
          }
          return (
            <Section key={f.id} f={f} active={active} onClear={clear} target={targetEl}>
              {(f.type === "checkbox" || f.type === "multiselect" || f.type === "select") && (
                <CheckboxFacet f={f} options={facets.optionsFor(f)} value={v} onChange={(x) => change(f.id, x)} showCount={showCounts} />
              )}
              {f.type === "numrange" && <NumFacet f={f} value={v} buckets={facets.bucketsFor(f)} stats={facets.statsFor(f)} onChange={(x) => change(f.id, x)} />}
              {f.type === "tier" && <TierFacet f={f} value={v} counts={facets.tiersFor(f)} onChange={(x) => change(f.id, x)} />}
              {f.type === "daterange" && <DateFacet f={f} value={v} onChange={(x) => change(f.id, x)} />}
            </Section>
          );
        })}
      </div>

      {applyMode && (
        <div data-testid="filter-panel-apply-bar" className="shrink-0 border-t border-[color:var(--glass-border)] p-2.5 flex items-center gap-1.5 bg-[color:var(--surface)]">
          <button data-testid="filter-panel-apply" onClick={applyDraft} disabled={!dirty}
            className="flex-1 text-[11px] font-medium px-3 py-1.5 rounded-xl bg-[color:var(--brand-ocean)] text-white disabled:opacity-50">
            {dirty ? "Apply changes" : "Applied"}
          </button>
          <button data-testid="filter-panel-apply-cancel" onClick={cancelDraft} disabled={!dirty}
            className="text-[11px] px-3 py-1.5 rounded-xl border border-[color:var(--glass-border)] text-[color:var(--ink-2)] disabled:opacity-50">
            Cancel
          </button>
        </div>
      )}

      {((total && settings.show_results !== false) || onExportCsv) && (
        <div className="shrink-0 border-t border-[color:var(--glass-border)] p-2.5 space-y-1.5 bg-[color:var(--surface)]">
          {total && settings.show_results !== false && (
            <button data-testid="filter-panel-results" onClick={() => drawer && onOpenChange(false)}
              className="w-full flex items-center justify-center gap-1.5 text-[11px] font-medium px-3 py-2 rounded-xl bg-[color:var(--brand-ocean)] text-white hover:opacity-90">
              <Check className="w-3.5 h-3.5" />Show {nf.format(total.count ?? 0)} result{(total.count ?? 0) === 1 ? "" : "s"}
              {total.grand_total ? <span className="opacity-70">of {nf.format(total.grand_total)}</span> : null}
            </button>
          )}
          {onExportCsv && (
            <button data-testid="filter-panel-export-csv" onClick={onExportCsv}
              className="w-full flex items-center justify-center gap-1.5 text-[11px] px-3 py-1.5 rounded-xl border border-[color:var(--glass-border)] text-[color:var(--ink-2)] hover:bg-[color:var(--surface-2)]">
              <Download className="w-3.5 h-3.5" />Export filtered CSV
            </button>
          )}
        </div>
      )}
    </>
  );

  if (drawer) {
    return (
      <>
        <div data-testid="filter-panel-backdrop" onClick={() => onOpenChange(false)}
          className={cn("fixed inset-0 z-40 bg-black/40 transition-opacity duration-200", open ? "opacity-100" : "opacity-0 pointer-events-none")} />
        <aside data-testid="filter-panel-drawer" role="dialog" aria-label="Filters" aria-modal="true"
          className={cn("fixed left-0 top-0 bottom-0 z-50 w-[86vw] max-w-[320px] flex flex-col bg-[color:var(--surface)] shadow-2xl transition-transform duration-200",
            open ? "translate-x-0" : "-translate-x-full")}>
          {body}
        </aside>
      </>
    );
  }

  if (!open) return null;

  return (
    <aside data-testid="filter-panel" aria-label="Filters"
      style={{ width: Number(settings.width) || PANEL_W }}
      className="shrink-0 self-start sticky top-2 max-h-[calc(100vh-88px)] flex flex-col rounded-2xl border border-[color:var(--glass-border)] bg-[color:var(--surface)] overflow-hidden">
      {body}
    </aside>
  );
}

/** Small button that re-opens the panel once hidden (desktop) or opens the drawer (mobile). */
// Saved filter sets: apply a favourite combination in one click, or store the current one.
function SavedSets({ sets = [], chips = [], editable = false, onApply, onSave, onDelete }) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [shared, setShared] = useState(false);
  const save = () => {
    const n = name.trim();
    if (!n) return;
    onSave?.({ name: n, shared: editable && shared });
    setName(""); setShared(false); setAdding(false);
  };
  return (
    <div data-testid="filter-sets" className="shrink-0 px-3 py-2 border-b border-[color:var(--glass-border)] space-y-1.5">
      <div className="flex items-center gap-1.5">
        <Bookmark className="w-3 h-3 text-[color:var(--ink-3)]" />
        <span className="text-[10px] font-semibold uppercase tracking-wide text-[color:var(--ink-3)] flex-1">Saved sets</span>
        {!adding && (
          <button data-testid="filter-sets-add" onClick={() => setAdding(true)} disabled={!chips.length}
            title={chips.length ? "Save the current filters as a set" : "Pick some filters first"}
            className={cn("flex items-center gap-1 text-[10px]",
              chips.length ? "text-[color:var(--brand-ocean)] hover:underline" : "text-[color:var(--ink-3)] opacity-50 cursor-default")}>
            <BookmarkPlus className="w-3 h-3" />Save
          </button>
        )}
      </div>
      {adding && (
        <div className="space-y-1.5">
          <Input data-testid="filter-sets-name" autoFocus value={name} onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setAdding(false); }}
            placeholder="Name this set…" className="h-7 text-[11px] bg-[color:var(--surface-2)]" />
          {editable && (
            <label className="flex items-center gap-1.5 text-[10px] text-[color:var(--ink-3)] cursor-pointer">
              <input data-testid="filter-sets-shared" type="checkbox" className="accent-[color:var(--brand-ocean)]"
                checked={shared} onChange={(e) => setShared(e.target.checked)} />
              <Users className="w-3 h-3" />Share with everyone who views this dashboard
            </label>
          )}
          <div className="flex gap-1.5">
            <button data-testid="filter-sets-save" onClick={save}
              className="text-[10px] px-2 py-1 rounded-full bg-[color:var(--brand-ocean)] text-white">Save set</button>
            <button data-testid="filter-sets-cancel" onClick={() => { setAdding(false); setName(""); }}
              className="text-[10px] px-2 py-1 rounded-full text-[color:var(--ink-3)] hover:bg-[color:var(--surface-2)]">Cancel</button>
          </div>
        </div>
      )}
      {sets.length === 0 && !adding && (
        <p className="text-[10px] text-[color:var(--ink-3)] italic">Filter, then hit Save to keep this combination.</p>
      )}
      {sets.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {sets.map((s) => (
            <span key={s.id} data-testid={`filter-set-${s.id}`}
              className="inline-flex items-center gap-1 text-[10px] rounded-full pl-2 pr-1 py-0.5 bg-[color:var(--surface-2)] text-[color:var(--ink-2)] hover:bg-[color:rgba(43,78,255,0.10)]">
              <button data-testid={`filter-set-apply-${s.id}`} onClick={() => onApply?.(s)} className="max-w-[128px] truncate">
                {s.name}
              </button>
              {s.shared && <Users className="w-2.5 h-2.5 opacity-60" title="Shared by the author" />}
              {(s.mine || s.local) && (
                <button data-testid={`filter-set-delete-${s.id}`} onClick={() => onDelete?.(s)} title="Delete this set"
                  className="p-0.5 rounded-full hover:bg-[color:var(--surface)] hover:text-[color:var(--danger)]">
                  <Trash2 className="w-2.5 h-2.5" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export function FilterPanelToggle({ open, onOpenChange, count = 0, drawer = false }) {
  return (
    <button data-testid="filter-panel-toggle" onClick={() => onOpenChange(!open)}
      className="flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-full border border-[color:var(--glass-border)] text-[color:var(--ink-2)] hover:bg-[color:var(--surface-2)] transition-colors">
      {open && !drawer ? <PanelLeftClose className="w-3.5 h-3.5" /> : <PanelLeftOpen className="w-3.5 h-3.5" />}
      Filters
      {count > 0 && <span className="ml-0.5 px-1.5 rounded-full bg-[color:var(--brand-ocean)] text-white text-[9px]">{count}</span>}
    </button>
  );
}

// Author-facing layout switch: the same setting as Tools -> Canvas & theme -> Filters, surfaced
// next to the filters themselves so the panel is discoverable without hunting through dialogs.
export function FilterLayoutSwitch({ value = "bar", onChange }) {
  const opts = [
    ["bar", PanelTop, "Top bar"],
    ["panel", PanelLeft, "Side panel"],
    ["both", Columns2, "Bar + panel"],
  ];
  return (
    <div data-testid="filter-layout-switch" title="Where these filters appear"
      className="flex items-center gap-0.5 p-0.5 rounded-full border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] shrink-0">
      {opts.map(([key, Icon, label]) => (
        <button key={key} data-testid={`filter-layout-${key}`} onClick={() => onChange(key)} title={label}
          aria-pressed={value === key}
          className={cn("flex items-center gap-1 text-[10px] px-2 py-1 rounded-full transition-colors",
            value === key ? "bg-[color:var(--brand-ocean)] text-white" : "text-[color:var(--ink-3)] hover:text-[color:var(--ink)]")}>
          <Icon className="w-3 h-3" />{label}
        </button>
      ))}
    </div>
  );
}

export { BLANK };
